// ============================================
// narb-auth/src/accessibility/AccessibilityToggle.jsx
// ============================================
// Universal accessibility toggle button. Fixed top-right corner of
// every screen of every Narbodica app.
//
// Per the Layer 1+2 design:
//   - Universal accessibility symbol (♿ stylised) — instantly recognisable
//   - Accent-coloured per app — accepts an `accentColor` prop so each
//     app can render the icon in its palette without changing the symbol
//   - Tab-order priority — first or second focusable element on the page
//   - Keyboard-activated — Enter or Space opens the sheet
//   - aria-label is descriptive enough for screen readers to announce
//     immediately
//   - Position fixed; iOS safe-area inset aware
// ============================================

import React, { useEffect } from 'react';
import { useAccessibilityStore } from './accessibilityStore.js';
import AccessibilitySheet from './AccessibilitySheet.jsx';

/**
 * @param {string} accentColor - CSS colour for the icon stroke. Each app
 *   passes its accent colour. Defaults to a neutral mid-grey so the toggle
 *   is visible without being styled if no colour is provided.
 * @param {string} [token] - auth token, passed through to the sheet for persistence
 * @param {string} [apiBase] - API base URL, passed through to the sheet for persistence
 */
export default function AccessibilityToggle({ accentColor = '#666', token, apiBase }) {
  const sheetOpen = useAccessibilityStore((s) => s.sheetOpen);
  const openSheet = useAccessibilityStore((s) => s.openSheet);
  const closeSheet = useAccessibilityStore((s) => s.closeSheet);

  // Escape key closes the sheet
  useEffect(() => {
    if (!sheetOpen) return;
    const handler = (e) => {
      if (e.key === 'Escape') closeSheet();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sheetOpen, closeSheet]);

  return (
    <>
      <button
        type="button"
        onClick={openSheet}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openSheet();
          }
        }}
        aria-label="Accessibility settings — open to adjust visual, audio, and interaction preferences"
        aria-expanded={sheetOpen}
        aria-controls="narbodica-accessibility-sheet"
        style={{
          position: 'fixed',
          top: 'env(safe-area-inset-top, 8px)',
          right: 'env(safe-area-inset-right, 8px)',
          marginTop: '8px',
          marginRight: '8px',
          zIndex: 9999,
          width: '44px',
          height: '44px',
          minWidth: '44px',
          minHeight: '44px',
          borderRadius: '50%',
          border: `2px solid ${accentColor}`,
          background: 'rgba(255, 255, 255, 0.95)',
          WebkitBackdropFilter: 'blur(8px)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          padding: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          transition: 'transform 150ms ease, box-shadow 150ms ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.08)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
        }}
      >
        {/* Universal accessibility symbol — stylised human figure with arms outstretched.
            SVG so it's crisp at any size and colour-customisable per app. */}
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke={accentColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          {/* Head */}
          <circle cx="12" cy="4" r="2" fill={accentColor} stroke="none" />
          {/* Arms outstretched */}
          <path d="M5 9 L19 9" />
          {/* Torso + legs */}
          <path d="M12 7 L12 14" />
          <path d="M12 14 L8 21" />
          <path d="M12 14 L16 21" />
        </svg>
      </button>

      {sheetOpen && (
        <AccessibilitySheet
          token={token}
          apiBase={apiBase}
          accentColor={accentColor}
          onClose={closeSheet}
        />
      )}
    </>
  );
}
